import type { Node, Edge } from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import type { Department, User, ShowOrgSettingsResponse } from '@bliks-io/contracts';

export type OrgInfo = Pick<ShowOrgSettingsResponse['data'], 'name' | 'logo'>;

/** User fields a department node renders; `role` is the user's Stelle (mainRole). */
export interface OrganigramNodeUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: User['avatar'];
  role?: { _id: string; name: string };
}

function toNodeUser(
  user: Pick<User, '_id' | 'firstName' | 'lastName' | 'email' | 'avatar' | 'mainRole'>
): OrganigramNodeUser {
  return {
    _id: user._id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    avatar: user.avatar,
    role: user.mainRole ? { _id: user.mainRole._id, name: user.mainRole.name } : undefined
  };
}

export interface DepartmentNodeData extends Record<string, unknown> {
  departmentId: string;
  label: string;
  name: string;
  abbreviation?: string;
  manager?: OrganigramNodeUser;
  members?: OrganigramNodeUser[];
  memberCount?: number;
  level: number;
  isExpanded?: boolean;
  onToggleExpand?: (departmentId: string) => void;
}

export interface OrganizationNodeData extends Record<string, unknown> {
  name?: string;
  logo?: OrgInfo['logo'];
  level: 0;
}

/**
 * Parse department path to extract hierarchy information
 * Path format: "<rootId>#<parentId>#<_id>" or just "<_id>" for root
 */
export function parseDepartmentPath(path: string | undefined): {
  segments: string[];
  level: number;
  parentId: string | null;
} {
  if (!path) {
    return { segments: [], level: 1, parentId: null };
  }

  const segments = path.split('#');
  const level = segments.length;
  const parentId = segments.length > 1 ? segments[segments.length - 2] : null;

  return { segments, level, parentId };
}

export interface BuildOrganigramNodesAndEdgesOptions {
  departments: Department[];
  orgMembers?: User[];
  expandedDepartments?: Set<string>;
  organization?: OrgInfo;
}

/**
 * Build React Flow nodes and edges from flat department list
 */
export function buildOrganigramNodesAndEdges(options: BuildOrganigramNodesAndEdgesOptions): {
  nodes: Node[];
  edges: Edge[];
} {
  const { departments, orgMembers, expandedDepartments, organization } = options;
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Add root organization node if organization data is provided
  if (organization) {
    nodes.push({
      id: 'org-root',
      type: 'organizationNode',
      position: { x: 0, y: 0 },
      data: {
        name: organization.name,
        logo: organization.logo,
        level: 0
      } as OrganizationNodeData
    });
  }

  // Create nodes for each department
  departments.forEach((department) => {
    const { level, parentId } = parseDepartmentPath(department.path);

    // Filter members for this department (excluding the manager since they're shown separately)
    const departmentMembers =
      orgMembers?.filter(
        (member) =>
          member.department?._id === department._id && member._id !== department.manager?._id
      ) || [];

    nodes.push({
      id: department._id,
      type: 'departmentNode',
      position: { x: 0, y: 0 }, // Will be calculated by layout
      data: {
        departmentId: department._id,
        label: department.name,
        name: department.name,
        abbreviation: department.abbreviation,
        manager: department.manager ? toNodeUser(department.manager) : undefined,
        members: departmentMembers.map(toNodeUser),
        memberCount: departmentMembers.length,
        level,
        isExpanded: expandedDepartments?.has(department._id) || false
      }
    });

    // Create edge to parent or to org root for level 1 departments
    if (parentId) {
      edges.push({
        id: `e-${parentId}-${department._id}`,
        source: parentId,
        target: department._id,
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: '#d1d5db',
          strokeWidth: 2
        }
      });
    } else if (organization) {
      // Level 1 department - connect to org root
      edges.push({
        id: `e-org-root-${department._id}`,
        source: 'org-root',
        target: department._id,
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: '#d1d5db',
          strokeWidth: 2
        }
      });
    }
  });

  return { nodes, edges };
}

/**
 * Calculate the dynamic height of a node based on its expansion state
 */
export function calculateNodeHeight(
  isExpanded: boolean,
  memberCount: number,
  level?: number
): number {
  // Organization root node (level 0) has fixed height
  if (level === 0) {
    return 80;
  }

  // Base height includes: header (~44px) + manager section (~80px) + footer (~32px) + padding
  const baseHeight = 200;

  if (!isExpanded || memberCount === 0) {
    return baseHeight;
  }

  // Each member card is approximately 60px (48px card + 8px spacing + 4px border)
  const memberCardHeight = 60;

  // Members section has max-h-[200px] with overflow-y-auto
  // Add padding, border, and "Members" label
  const membersSectionOverhead = 20; // padding + borders
  const maxMembersHeight = 200; // max-height of scrollable area

  const membersContentHeight = Math.min(memberCount * memberCardHeight, maxMembersHeight);

  return baseHeight + membersContentHeight + membersSectionOverhead;
}

/**
 * Apply dagre layout algorithm to position nodes hierarchically
 */
export function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB'
): Node[] {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure graph
  const nodeWidth = 280;

  dagreGraph.setGraph({
    rankdir: direction, // TB = top to bottom, LR = left to right
    nodesep: 80, // Horizontal spacing between nodes
    ranksep: 150, // Vertical spacing between ranks (increased from 100)
    marginx: 50,
    marginy: 50
  });

  // Add nodes to dagre graph with dynamic heights
  nodes.forEach((node) => {
    const data = node.data as DepartmentNodeData | OrganizationNodeData;
    const isExpanded =
      node.type === 'departmentNode' ? (data as DepartmentNodeData).isExpanded || false : false;
    const memberCount =
      node.type === 'departmentNode' ? (data as DepartmentNodeData).memberCount || 0 : 0;
    const nodeHeight = calculateNodeHeight(isExpanded, memberCount, data.level);

    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Update node positions
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const data = node.data as DepartmentNodeData | OrganizationNodeData;
    const isExpanded =
      node.type === 'departmentNode' ? (data as DepartmentNodeData).isExpanded || false : false;
    const memberCount =
      node.type === 'departmentNode' ? (data as DepartmentNodeData).memberCount || 0 : 0;
    const nodeHeight = calculateNodeHeight(isExpanded, memberCount, data.level);

    return {
      ...node,
      position: {
        // Center the node based on its dimensions
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2
      }
    };
  });

  return layoutedNodes;
}

export interface BuildOrganigramLayoutOptions {
  departments: Department[];
  direction?: 'TB' | 'LR';
  orgMembers?: User[];
  expandedDepartments?: Set<string>;
  organization?: OrgInfo;
}

/**
 * Main function to build complete organigram layout
 */
export function buildOrganigramLayout(options: BuildOrganigramLayoutOptions): {
  nodes: Node[];
  edges: Edge[];
} {
  const { departments, direction = 'TB', orgMembers, expandedDepartments, organization } = options;

  // Build nodes and edges
  const { nodes, edges } = buildOrganigramNodesAndEdges({
    departments,
    orgMembers,
    expandedDepartments,
    organization
  });

  // Apply layout
  const layoutedNodes = applyDagreLayout(nodes, edges, direction);

  return {
    nodes: layoutedNodes,
    edges
  };
}
